import { PixProps } from '../props';
import { Copy } from '@starsoft/common/components';
import styles from './styles.module.scss';
import { QRCodeSVG } from 'qrcode.react';
import { NotificationsService, WalletService } from '@starsoft/common/services';
import { useTranslation } from '../../../../../../../../../hooks';
import { useNotificationService } from '@starsoft/common/hooks/useNotificationService';
import { getMoneyOrAmount } from '@starsoft/common/utils';

/**
 * QrCodePendingContent Component
 * This component is responsible for displaying the pending QR code content for a deposit transaction.
 * It includes the QR code, the amount to be deposited, and additional information related to the transaction.
 *
 * @param {PixProps} props - The properties for the component.
 * @param {object} props.deposit - The deposit object containing details such as amount, coin, and QR code content.
 *
 * @returns {JSX.Element} The rendered component displaying the pending QR code content.
 *
 * @example
 * // Usage example:
 * const deposit = { amount: 100, coin: 'USD', qrCode: { content: 'sampleQRCodeContent' } };
 * <QrCodePendingContent deposit={deposit} />
 */
export default function QrCodePendingContent({ deposit }: PixProps) {
  // Hook to handle translations for the 'deposit' namespace
  const { t } = useTranslation('deposit');
  // Hook to use the notification service for displaying success messages
  const NotificationsService: NotificationsService<unknown> =
    useNotificationService();

  /**
   * notifyCopy Function
   * This function triggers a success notification when the QR code content is copied.
   */
  function notifyCopy() {
    NotificationsService.success({
      title: 'notification_copy_title',
      message: 'notification_copy_text',
    });
  }

  return (
    <div className={styles.container}>
      <h6 className={styles.container__title}>{t('pix_pending_title')}</h6>

      <div className={styles.container__wrapper}>
        <div className={styles.container__value}>
          {WalletService.maskCurrency({
            amount: getMoneyOrAmount(deposit?.amount),
            coin: deposit?.coin,
          })}
        </div>

        <div className={styles.container__qrcode}>
          {/* Render the QR code if content is available */}
          {deposit?.qrCode?.content && (
            <QRCodeSVG value={deposit?.qrCode?.content as string} size={150} />
          )}
        </div>
      </div>

      <h5
        className={`${styles.container__title} ${styles['container__title--center']}`}
      >
        {t('pix_code_title')}
      </h5>

      <Copy
        label={deposit?.qrCode?.content}
        textToCopy={deposit?.qrCode?.content}
        callback={notifyCopy}
        secondaryStyles
      />

      <div className={styles.container__divider} />

      <div className={styles.container__row}>
        <i className={`${styles.container__icon} fa-solid fa-clock`} />
        <span className={styles.container__text}>{t('pix_credited')}</span>
      </div>

      <div className={styles.container__alert}>{t('pix_payment_alert')}</div>
    </div>
  );
}
